/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

export const arrayUtils = {
  getArrayInterSection: function getArrayInterSection<T>(a: Array<T>, b: Array<T>) {
    const bAsSet = new Set(b);
    return a.filter((item) => bAsSet.has(item));
  },
  getArraysIntersect: function getArraysIntersect<T>(a: Array<T>, b: Array<T>) {
    return arrayUtils.getArrayInterSection(a, b).length > 0;
  },
};

export const debounceWithReturnedPromise = <T extends (...args: any[]) => any>(fn: T, delay: number) => {
  let timeoutId: ReturnType<typeof setTimeout>;

  let pendingResolvers: Array<{
    resolve: (value: Awaited<ReturnType<T>>) => void;
    reject: (reason?: any) => void;
  }> = [];

  return function (this: any, ...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> {
    return new Promise((resolve, reject) => {
      pendingResolvers.push({ resolve, reject });

      clearTimeout(timeoutId);

      timeoutId = setTimeout(async () => {
        const resolversToProcess = pendingResolvers;
        pendingResolvers = [];
        try {
          const result = await fn.apply(this, args);
          resolversToProcess.forEach(({ resolve }) => resolve(result));
        } catch (error) {
          resolversToProcess.forEach(({ reject }) => reject(error));
        }
      }, delay);
    });
  };
};

export const lowerFirstHtmlText = (html: string): string => {
  const template = document.createElement("template");
  template.innerHTML = html;

  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      return /\p{L}/u.test(node.textContent ?? "") ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });

  const firstTextNode = walker.nextNode();

  if (firstTextNode?.textContent) {
    firstTextNode.textContent = firstTextNode.textContent.replace(/\p{L}/u, (letter) => letter.toLowerCase());
  }

  return template.innerHTML;
};

export function getIriOfSubEntity(entity: Record<string, any>, subEntityKey: string): string | null {
  if (!entity[subEntityKey]) {
    return null;
  }

  if (typeof entity[subEntityKey] === "string") {
    return entity[subEntityKey];
  }

  return entity[subEntityKey]["@id"] ?? null;
}
